import { formatEther, formatUnits } from "viem";
import Erc20Permit from "@shared/src/lib/abis/Erc20Permit";
import { ReactNode, useEffect } from "react";
import { useP2PConfig } from "@shared/src/hooks/useP2PConfig";
import { getTokenIconUrl } from "@sdk/utils/config";
import { useHostStore } from "../stores/useHostStore";
import { useQuery } from "@tanstack/react-query";
import { ERC20_BALANCE_QUERY_KEY, ERC20_DECIMALS_QUERY_KEY, NATIVE_BALANCE_QUERY_KEY } from "../lib/queryKeys";
import { getReadClient, readContract } from "../components/utils/blockchain";

interface UseBalancesProps {
  enabled?: boolean;
}

interface UseBalanceProps {
  tokenAddress?: `0x${string}`;
  enabled?: boolean;
  chainId?: number;
  test?: string;
}

export interface TokenBalance {
  icon: ReactNode;
  name: string;
  subtext: string;
  amount: string;
  value: string;
  rawBalance: bigint;
  address: `0x${string}`;
  decimals: number;
  rawAmount: string;
}

export interface useBalanceResult {
  balance: TokenBalance | null;
  isLoading: boolean;
  refetch: () => void;
  isFetching?: boolean;
}

export interface useAllBalancesResult {
  balances: TokenBalance[] | null;
  total: string;
  formattedTotal: string;
  isLoading: boolean;
  queryKey: readonly unknown[];
  refetch: () => void;
  isFetching: boolean;
}

export const getTokenIcon = (symbol: string): ReactNode => {
  const sdk = useHostStore.getState().sdk;
  const walletClient = sdk?.auth.getWalletClient();
  const activeChain = walletClient?.chain;
  const size = symbol.toLowerCase() === activeChain?.nativeCurrency?.symbol ? 15 : 20;
  return <img alt="token icon" width={size} height={size} src={getTokenIconUrl(symbol)} />;
};

// Hook for fetching a single token balance
export function useTokenBalance({ tokenAddress, enabled = true, chainId }: UseBalanceProps): useBalanceResult {
  const sdk = useHostStore(state => state.sdk);
  const activeChain = useHostStore(state => state.activeChain);
  const walletClient = sdk?.auth.getWalletClient();
  const effectiveChainId = chainId || activeChain?.id;
  const effectiveChain = sdk?.config.chains.find(chain => chain.id === effectiveChainId);

  const activeAccount = walletClient?.account;

  const tokenExistsInNetwork = sdk?.config.tokens?.find(
    t => t.address.toLowerCase() === tokenAddress?.toLowerCase() && t.networkId === effectiveChainId,
  );
  if (!tokenExistsInNetwork) {
    console.warn("refetching balance Token not found in network", { tokenAddress, effectiveChainId });
  }

  // Determine if the hooks should be enabled
  const accountEnabled = !!activeAccount && enabled;
  const nativeEnabled = accountEnabled && !tokenAddress;
  const tokenEnabled = accountEnabled && !!tokenAddress && !!tokenExistsInNetwork;

  useEffect(() => {
    if (!effectiveChainId) return;
    const hostStore = useHostStore.getState();
    hostStore.setAndSwitchChain(effectiveChainId);
  }, [effectiveChainId]);

  // Native token balance hook
  const {
    data: nativeBalance,
    isLoading: nativeLoading,
    refetch: refetchNative,
    isFetching: isFetchingNative,
  } = useQuery({
    queryKey: NATIVE_BALANCE_QUERY_KEY(activeAccount?.address || "", effectiveChainId || 0),
    queryFn: async () => {
      if (!activeAccount || !effectiveChain) throw new Error("No active account or chain");
      const client = await getReadClient({ walletClient, chain: effectiveChain });
      return client.getBalance({
        address: activeAccount.address,
      });
    },
    enabled: nativeEnabled,
  });
  // ERC20 token balance hook
  const {
    data: tokenBalance,
    isLoading: tokenLoading,
    refetch: refetchToken,
    isFetching,
    status,
    error,
  } = useQuery({
    queryKey: ERC20_BALANCE_QUERY_KEY(activeAccount?.address || "", effectiveChainId || 0, tokenAddress || ""),
    queryFn: () => {
      if (!activeAccount || !effectiveChain) throw new Error("No active account or chain");
      if (!tokenAddress) throw new Error("No token address");
      return readContract({
        walletClient,
        chain: effectiveChain,
        args: {
          address: tokenAddress,
          abi: Erc20Permit,
          functionName: "balanceOf",
          args: [activeAccount.address],
        },
      });
    },
    enabled: tokenEnabled,
  });

  // Token decimal hook
  const { data: decimals, isLoading: decimalsLoading } = useQuery({
    queryKey: ERC20_DECIMALS_QUERY_KEY(tokenAddress || "", effectiveChainId || 0, activeAccount?.address || ""),
    queryFn: () => {
      if (!activeAccount || !effectiveChain) throw new Error("No active account or chain");
      if (!tokenAddress) throw new Error("No token address");
      return readContract({
        walletClient,
        chain: effectiveChain,
        args: {
          address: tokenAddress,
          abi: Erc20Permit,
          functionName: "decimals",
          args: [],
        },
      });
    },
    enabled: tokenEnabled,
  });

  // Calculate the balance based on conditions
  let balance: TokenBalance | null = null;
  let isLoading = false;
  let isFetchingCombined = false;
  let refetch = () => {};

  if (!activeAccount) {
    // No active account case
    balance = null;
    isLoading = false;
    refetch = () => {};
  } else if (!tokenAddress) {
    // Native token case
    isLoading = nativeLoading;
    isFetchingCombined = !!isFetchingNative;
    refetch = refetchNative;

    if (nativeBalance) {
      const symbol = effectiveChain?.nativeCurrency.symbol || "ETH";
      const usdRate = symbol === activeChain?.nativeCurrency?.symbol || "ETH" ? 3000 : 1;
      const formattedAmount = formatEther(nativeBalance);
      const usdValue = (parseFloat(formattedAmount) * usdRate).toFixed(2);

      balance = {
        icon: getTokenIcon(symbol),
        name: symbol,
        subtext: symbol,
        amount: `${formattedAmount} ${symbol}`,
        value: `$${usdValue}`,
        rawBalance: nativeBalance,
        address: "0x",
        decimals: 18,
        rawAmount: formattedAmount,
      };
    }
  } else {
    // ERC20 token case
    isLoading = tokenLoading;
    isFetchingCombined = !!isFetching;
    refetch = refetchToken;

    // Get token details
    const tokenInfo = sdk?.config.tokens?.find(
      t => t.address.toLowerCase() === tokenAddress.toLowerCase() && t.networkId === effectiveChainId,
    );

    console.log("tokenInfo", { tokenInfo, tokenAddress, effectiveChainId, chainId });

    const tokenSymbol = tokenInfo?.symbol || "Unknown";
    const displaySymbol = tokenSymbol.toLowerCase().includes("eth")
      ? "ETH"
      : tokenSymbol.toLowerCase().includes("usdt")
      ? "USDT"
      : tokenSymbol.toLowerCase().includes("usdc")
      ? "USDC"
      : tokenSymbol;

    const usdRate = 1;
    const rawAmount =
      tokenBalance && decimals ? parseFloat(formatUnits(tokenBalance as bigint, decimals)).toFixed(6) : "0.000000";
    const usdValue = (parseFloat(rawAmount) * usdRate).toFixed(2);

    balance = {
      icon: getTokenIcon(displaySymbol),
      name: displaySymbol,
      subtext: tokenInfo?.name || displaySymbol,
      amount: `${rawAmount} ${displaySymbol}`,
      value: `$${usdValue}`,
      rawBalance: tokenBalance ?? BigInt(0),
      address: tokenAddress,
      decimals: decimals ?? 0,
      rawAmount,
    };
  }

  return {
    balance,
    isLoading,
    refetch,
    isFetching: isFetchingCombined,
  };
}
